import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { resolveWorkspaceTab } from '@/lib/workspaceTabs';

// The Workbench: an app-wide registry of the (max ~8) live working sessions —
// MODEL editors, INSIGHT editors, and AI EXPLORE sessions. Each resource is a
// singleton by `key`; opening it again focuses the existing session. Sessions
// are kept alive across navigation, persisted (identity only) across reloads,
// and shown in the bottom dock. Everything else in the app is a plain screen
// and never enters the Workbench.

const WorkspaceCtx = createContext(null);
const STORAGE_KEY = 'inzara.workbench.v2';
const MAX_TABS = 8;

export function WorkspaceProvider({ children }) {
  const [tabs, setTabs] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) { const p = JSON.parse(raw); if (Array.isArray(p?.tabs)) return p.tabs; }
    } catch { /* corrupted — start fresh */ }
    return [];
  });
  const [activeKey, setActiveKey] = useState(null);
  const [dirtyKeys, setDirtyKeys] = useState(() => new Set());
  const [saverKeys, setSaverKeys] = useState(() => new Set());
  const [pendingCloseKey, setPendingCloseKey] = useState(null);
  const saversRef = useRef({}); // key -> async save fn

  // The workbench belongs to a single signed-in identity. Reset it whenever the
  // identity changes — critically, when an admin starts/stops IMPERSONATING a
  // user — so the impersonated session never inherits the admin's open tabs
  // (and vice-versa on return).
  const { user } = useAuth();
  const currentUserId = user?.user_id || null;
  const _readOwner = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').owner || null; } catch { return null; } };
  useEffect(() => {
    if (!currentUserId) return; // wait until auth resolves
    const storedOwner = _readOwner();
    if (storedOwner && storedOwner !== currentUserId) {
      setTabs([]);
      setActiveKey(null);
    }
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...raw, owner: currentUserId }));
    } catch { /* quota */ }
  }, [currentUserId]);

  useEffect(() => {
    // Persist identity only (never dirty/saver runtime state), tagged with the
    // owning user so a different identity can't reuse it.
    try {
      const slim = tabs.map(({ key, type, title, path, pinned, customTitle }) => ({ key, type, title, path, pinned, customTitle }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs: slim, owner: currentUserId || _readOwner() }));
    } catch { /* quota */ }
  }, [tabs, currentUserId]);

  const openTab = useCallback((desc) => {
    if (!desc || !desc.key) return;
    setActiveKey(desc.key);
    setTabs(prev => {
      const existing = prev.find(t => t.key === desc.key);
      if (existing) {
        return prev.map(t => t.key === desc.key
          ? { ...t, path: desc.path || t.path, type: desc.type || t.type, _t: Date.now() }
          : t);
      }
      let next = [...prev, {
        key: desc.key, type: desc.type || 'insight',
        title: desc.title || 'Untitled', path: desc.path,
        pinned: !!desc.pinned, _t: Date.now(),
      }];
      // Over the cap: evict the oldest session that is not pinned, not active,
      // and has no unsaved edits. If none qualify, allow a temporary overflow.
      if (next.length > MAX_TABS) {
        const victim = next
          .filter(t => !t.pinned && t.key !== desc.key && !dirtyKeys.has(t.key))
          .sort((a, b) => (a._t || 0) - (b._t || 0))[0];
        if (victim) next = next.filter(t => t.key !== victim.key);
      }
      return next;
    });
  }, [dirtyKeys]);

  const setTitle = useCallback((key, title) => {
    if (!key || !title) return;
    setTabs(prev => {
      const t = prev.find(x => x.key === key);
      // A user-set custom name always wins over page-derived titles.
      if (!t || t.customTitle || t.title === title) return prev;
      return prev.map(x => x.key === key ? { ...x, title } : x);
    });
  }, []);

  const renameTab = useCallback((key, title) => {
    const name = (title || '').trim();
    setTabs(prev => prev.map(x => x.key === key
      ? { ...x, customTitle: name || undefined, title: name || x.title }
      : x));
  }, []);

  const togglePin = useCallback((key) => {
    setTabs(prev => prev.map(t => t.key === key ? { ...t, pinned: !t.pinned } : t));
  }, []);

  const closeTab = useCallback((key) => {
    setTabs(prev => prev.filter(t => t.key !== key));
    setDirtyKeys(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n; });
    setSaverKeys(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n; });
    delete saversRef.current[key];
  }, []);

  // ── Dirty tracking ────────────────────────────────────────────────────────
  const setDirty = useCallback((key, dirty) => {
    if (!key) return;
    setDirtyKeys(prev => {
      const has = prev.has(key);
      if (dirty && has) return prev;
      if (!dirty && !has) return prev;
      const n = new Set(prev);
      if (dirty) n.add(key); else n.delete(key);
      return n;
    });
  }, []);
  const isDirty = useCallback((key) => dirtyKeys.has(key), [dirtyKeys]);

  const registerSaver = useCallback((key, fn) => {
    if (!key || typeof fn !== 'function') return;
    saversRef.current[key] = fn;
    setSaverKeys(prev => { if (prev.has(key)) return prev; const n = new Set(prev); n.add(key); return n; });
  }, []);
  const unregisterSaver = useCallback((key) => {
    delete saversRef.current[key];
    setSaverKeys(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n; });
  }, []);

  // ── Close with an unsaved-changes guard ─────────────────────────────────────
  // Explore sessions are auto-persisted server-side, so they never prompt.
  const requestClose = useCallback((key) => {
    const tab = tabs.find(t => t.key === key);
    if (tab && tab.type !== 'explore' && dirtyKeys.has(key)) {
      setPendingCloseKey(key);
      return false; // deferred to the dialog
    }
    closeTab(key);
    return true; // closed immediately
  }, [tabs, dirtyKeys, closeTab]);

  const resolvePendingClose = useCallback(async (action) => {
    const key = pendingCloseKey;
    if (!key) return;
    if (action === 'cancel') { setPendingCloseKey(null); return; }
    if (action === 'save' && saversRef.current[key]) {
      try { await saversRef.current[key](); }
      catch { setPendingCloseKey(null); return; } // save failed — keep the session
    }
    closeTab(key);
    setPendingCloseKey(null);
  }, [pendingCloseKey, closeTab]);

  const pendingCloseTab = useMemo(
    () => tabs.find(t => t.key === pendingCloseKey) || null,
    [tabs, pendingCloseKey]
  );

  // ── Split view — exactly two sessions side-by-side (session-only) ──────────
  const [split, setSplitState] = useState(null);
  const openSplit = useCallback((left, right) => {
    if (!left || !right || left.path === right.path) return;
    setSplitState({ leftPath: left.path, leftTitle: left.title, rightPath: right.path, rightTitle: right.title });
  }, []);
  const clearSplit = useCallback(() => setSplitState(null), []);

  // ── Track the current route as a session (open or focus) ───────────────────
  const { pathname, search } = useLocation();
  useEffect(() => {
    const desc = resolveWorkspaceTab(pathname, search);
    if (desc) openTab(desc);
    else setActiveKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  const value = useMemo(() => ({
    tabs, activeKey, setActiveKey, openTab, closeTab, requestClose, togglePin, setTitle, renameTab,
    isDirty, setDirty, dirtyKeys, saverKeys, registerSaver, unregisterSaver,
    pendingCloseTab, resolvePendingClose,
    split, openSplit, clearSplit,
  }), [tabs, activeKey, openTab, closeTab, requestClose, togglePin, setTitle, renameTab,
       isDirty, setDirty, dirtyKeys, saverKeys, registerSaver, unregisterSaver,
       pendingCloseTab, resolvePendingClose, split, openSplit, clearSplit]);

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export function useWorkspace() {
  return useContext(WorkspaceCtx);
}

// Pages call this to give their current session a meaningful title.
export function useWorkspaceTitle(title) {
  const ws = useContext(WorkspaceCtx);
  const setTitle = ws?.setTitle;
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!setTitle || !title) return;
    const desc = resolveWorkspaceTab(pathname, search);
    if (desc) setTitle(desc.key, title);
  }, [setTitle, title, pathname, search]);
}

// Editors call this to report unsaved-changes state for the current session,
// so closing/evicting it prompts to save. Clears on unmount.
export function useWorkspaceDirty(dirty) {
  const ws = useContext(WorkspaceCtx);
  const setDirty = ws?.setDirty;
  const { pathname, search } = useLocation();
  const key = resolveWorkspaceTab(pathname, search)?.key;
  useEffect(() => {
    if (!setDirty || !key) return;
    setDirty(key, !!dirty);
    return () => setDirty(key, false);
  }, [setDirty, key, dirty]);
}

// Editors call this to expose a save() handler for the "Save & close" prompt.
export function useWorkspaceSaver(saver) {
  const ws = useContext(WorkspaceCtx);
  const registerSaver = ws?.registerSaver;
  const unregisterSaver = ws?.unregisterSaver;
  const { pathname, search } = useLocation();
  const key = resolveWorkspaceTab(pathname, search)?.key;
  const saverRef = useRef(saver);
  useEffect(() => { saverRef.current = saver; }, [saver]);
  useEffect(() => {
    if (!registerSaver || !key || !saver) return;
    registerSaver(key, (...a) => saverRef.current?.(...a));
    return () => unregisterSaver?.(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerSaver, unregisterSaver, key, !!saver]);
}
